import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { useAuth as useOidcAuth } from 'react-oidc-context'
import { setAccessToken, setStepUpHandler } from '../store/authStore'
import { currentReturnTo } from './returnTo'
import { endDocsSession } from '../api/docs'

/**
 * A narrow view of the OIDC session.
 *
 * The shell owns the Keycloak session; everything downstream reads it through this interface
 * rather than through `react-oidc-context` directly. That keeps the provider swappable — the
 * module also runs inside the AboveStore shell, where the session belongs to somebody else — and
 * means tests stub one small surface instead of the whole OIDC library.
 */
export interface AuthContextValue {
  isAuthenticated: boolean
  isLoading: boolean
  /** Set when silent renew has failed, i.e. the session is over even if a stale token remains. */
  error: Error | null
  accessToken: string | null
  /**
   * Whoever this session is, as its issuer names them.
   *
   * That is the identity provider's subject, which is **not** the Relay user id — the server maps one
   * to the other. Nothing downstream may treat it as one; `/api/me` is what names the Relay user.
   */
  subject: string | null
  email: string | null
  displayName: string | null
  login: () => void
  logout: () => void
}

/**
 * Exported so a test, or a host shell, can provide the same value from its own session. Everything
 * downstream — `UserMenu`, `PermissionsContext`, every page — reads `useAuth()` and cannot tell which
 * provider it got, which is the point of the interface above.
 */
export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const oidc = useOidcAuth()
  const token = oidc.user?.access_token ?? null

  // Mirrors the token into the store plain `fetch` callers and the viewers read, on sign-in and on
  // every silent renew. Without this the store kept whatever it read at load, so the first sign-in
  // and every renewed session sent a missing or stale token.
  useEffect(() => {
    if (oidc.isLoading) return
    setAccessToken(token)
  }, [token, oidc.isLoading])

  // Step-up: a fresh sign-in (max_age=0), at the strength the relay asked for, in a popup so the
  // page and whatever the operator was doing survive it.
  useEffect(() => {
    setStepUpHandler(async (acrValues) => {
      const extra = acrValues ? { acr_values: acrValues } : {}
      try {
        // Into the store at once, so the retry that follows carries the new token rather than waiting for a render.
        const user = await oidc.signinPopup({ max_age: 0, ...extra })
        setAccessToken(user.access_token)
      } catch {
        // A blocked popup: the same, by redirect. The page reloads afterwards.
        await oidc.signinRedirect({ max_age: 0, ...extra, state: { returnTo: currentReturnTo() } })
      }
    })
    return () => setStepUpHandler(null)
  }, [oidc])

  const value: AuthContextValue = {
    isAuthenticated: oidc.isAuthenticated,
    isLoading: oidc.isLoading,
    error: oidc.error ?? null,
    accessToken: oidc.user?.access_token ?? null,
    subject: oidc.user?.profile.sub ?? null,
    email: oidc.user?.profile.email ?? null,
    displayName:
      (oidc.user?.profile.name as string | undefined) ??
      (oidc.user?.profile.preferred_username as string | undefined) ??
      oidc.user?.profile.email ??
      null,
    // Back to this page once signed in (shell/returnTo.ts).
    login: () => void oidc.signinRedirect({ state: { returnTo: currentReturnTo() } }),
    // The docs cookie goes with the session; signing out proceeds whether or not the relay answered.
    logout: () => void endDocsSession().catch(() => undefined).finally(() => void oidc.signoutRedirect()),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
